import React from "react";
import { MdRemove, MdAutoAwesome } from "react-icons/md";
import { IoPaperPlane } from "react-icons/io5";
import { Scrollbars } from "react-custom-scrollbars-2";
import {
  renderTrackMessages,
  renderThumbMessages,
  renderViewMessages,
} from "components/scrollbar/Scrollbar";
import QueryMessage from "./QueryMessage";
import {
  ChatMessage,
  formatTime,
  suggestedPrompts,
} from "./mockData";
import { dataQueryChat, DataQueryResponse } from "@services/cloudFunctions";
import { useTenant } from "@contexts/TenantContext";
import {
  inputStorageKey,
  loadMessages,
  saveMessages,
  purgeExpiredChats,
} from "./storage";

// --- Timestamp grouping ---
function getDateLabel(timestamp: string): string {
  const parsed = new Date(timestamp);
  if (isNaN(parsed.getTime())) return "Today"; // fallback for old time-only strings in localStorage

  const now = new Date();
  const today = now.toLocaleDateString("en-US");
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toLocaleDateString("en-US");

  const msgDate = parsed.toLocaleDateString("en-US");

  if (msgDate === today) return "Today";
  if (msgDate === yesterdayStr) return "Yesterday";
  return msgDate;
}

function DateSeparator(props: { label: string }) {
  return (
    <div className="my-3 flex items-center gap-3">
      <div className="h-px flex-1 bg-gray-200 dark:bg-navy-600" />
      <span className="text-[11px] font-medium text-gray-400">{props.label}</span>
      <div className="h-px flex-1 bg-gray-200 dark:bg-navy-600" />
    </div>
  );
}

// --- Typing indicator ---

function TypingIndicator() {
  return (
    <div className="mb-3 flex justify-start">
      <div className="flex items-center gap-1 rounded-r-xl rounded-bl-xl bg-lightPrimary px-5 py-4 dark:bg-navy-700">
        <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:0ms]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:150ms]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:300ms]" />
      </div>
    </div>
  );
}

// --- Empty state ---

function EmptyState(props: { onPromptClick: (prompt: string) => void }) {
  const { onPromptClick } = props;
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-900/20">
        <MdAutoAwesome className="h-8 w-8 text-brand-500" />
      </div>
      <h3 className="mt-4 text-base font-bold text-navy-700 dark:text-white">
        AI Data Query Assistant
      </h3>
      <p className="mt-2 text-sm text-gray-400">
        Ask me anything about your data. Powered by AI, I can show users,
        matches, cohorts, emails, and engagement metrics.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {suggestedPrompts.map((prompt) => (
          <button
            key={prompt}
            onClick={() => onPromptClick(prompt)}
            className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-navy-700 transition hover:border-brand-500 hover:text-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:border-brand-400 dark:hover:text-brand-400"
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Main panel ---

function DataQueryPanel(props: { isOpen: boolean; onClose: () => void }) {
  const { isOpen, onClose } = props;
  const { currentTenantId, currentUser } = useTenant();
  const userEmail = currentUser?.email ?? null;
  const [messages, setMessages] = React.useState<ChatMessage[]>(() => loadMessages(currentTenantId, userEmail));
  const [input, setInput] = React.useState(() => {
    try {
      return localStorage.getItem(inputStorageKey(currentTenantId, userEmail)) || "";
    } catch {
      return "";
    }
  });
  const [isTyping, setIsTyping] = React.useState(false);

  const [isOffline, setIsOffline] = React.useState(false);
  const scrollRef = React.useRef<Scrollbars>(null);
  const prevTenantId = React.useRef(currentTenantId);
  const prevEmail = React.useRef(userEmail);

  // Sweep expired chats (>30 days) on mount
  React.useEffect(() => {
    purgeExpiredChats();
  }, []);

  // When tenant or user changes, load the matching chat history
  React.useEffect(() => {
    if (prevTenantId.current !== currentTenantId || prevEmail.current !== userEmail) {
      prevTenantId.current = currentTenantId;
      prevEmail.current = userEmail;
      setMessages(loadMessages(currentTenantId, userEmail));
      try {
        setInput(localStorage.getItem(inputStorageKey(currentTenantId, userEmail)) || "");
      } catch {
        setInput("");
      }
      setIsTyping(false);
      setIsOffline(false);
    }
  }, [currentTenantId, userEmail]);

  // Persist messages to localStorage (scoped to tenant + user)
  React.useEffect(() => {
    saveMessages(messages, currentTenantId, userEmail);
  }, [messages, currentTenantId, userEmail]);

  // Persist input to localStorage (scoped to tenant + user)
  React.useEffect(() => {
    try {
      const key = inputStorageKey(currentTenantId, userEmail);
      if (input) {
        localStorage.setItem(key, input);
      } else {
        localStorage.removeItem(key);
      }
    } catch {
      // ignore
    }
  }, [input, currentTenantId, userEmail]);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollToBottom();
    }
  }, [messages, isTyping]);

  /**
   * Build conversation history from stored messages for AI context
   */
  const buildConversationHistory = (): Array<{ role: 'user' | 'assistant'; content: string }> => {
    return messages
      .filter((m) => m.type !== "error" && !m.flagged)
      .slice(-10)
      .map((m) => {
        let content = m.content;
        if (m.sender === "user") {
          // User messages: send as-is
        } else if (m.type === "table") {
          // Include full table so AI can reference previous results
          const headers = m.tableHeaders?.join(", ") || "";
          const rows = m.tableRows?.map((r) => r.join(", ")).join("\n") || "";
          content = `[Table: ${headers}]\n${rows}`;
        } else if (m.type === "summary") {
          const metrics = m.summaryMetrics?.map((s) => `${s.label}: ${s.value} (${s.change})`).join("; ")
            || ((m as any).summaryLabel ? `${(m as any).summaryLabel}: ${(m as any).summaryValue}` : content);
          content = `[Summary: ${metrics}]`;
        } else if (m.type === "list") {
          content = `[List: ${m.listItems?.join("; ") || content}]`;
        } else if (m.type === "file") {
          content = `[Exported CSV file: ${m.fileName}]`;
        } else if (m.type === "csv") {
          content = `[CSV data: ${m.content.substring(0, 500)}]`;
        }
        return {
          role: m.sender === "user" ? "user" as const : "assistant" as const,
          content,
        };
      });
  };

  /**
   * Convert headers + rows into a CSV string
   */
  const buildCsvString = (headers: string[], rows: string[][]): string => {
    const escapeCsvField = (field: unknown) => {
      const str = String(field ?? "");
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const headerLine = headers.map(escapeCsvField).join(",");
    const rowLines = rows.map((row) => row.map(escapeCsvField).join(","));
    return [headerLine, ...rowLines].join("\n");
  };

  /**
   * Map the AI response to the frontend ChatMessage format
   */
  const mapResponseToMessage = (response: DataQueryResponse): ChatMessage => {
    const base = {
      id: crypto.randomUUID(),
      sender: "system" as const,
      timestamp: formatTime(),
    };

    switch (response.type) {
      case "csv": {
        const headers = response.data.headers || [];
        const rows = response.data.rows || [];
        const csvContent = buildCsvString(headers, rows);
        const bytes = new Blob([csvContent]).size;
        const fileSize = bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
        const title = (response.title || "export").toLowerCase().replace(/\s+/g, "-");

        return {
          ...base,
          type: "file",
          content: csvContent,
          fileName: `latte-${title}.csv`,
          fileSize,
          fileType: "csv",
        };
      }

      case "table": {
        const tHeaders = response.data.headers || [];
        const tRows = response.data.rows || [];
        return {
          ...base,
          type: "table",
          content: buildCsvString(tHeaders, tRows),
          tableHeaders: tHeaders,
          tableRows: tRows,
        };
      }

      case "summary": {
        const metrics = response.data.metrics;
        if (metrics && metrics.length > 0) {
          return {
            ...base,
            type: "summary",
            content: response.data.message || response.title || "",
            summaryMetrics: metrics.map((m) => ({
              label: m.label,
              value: m.value,
              change: m.change,
              changeType: m.trend || "neutral",
            })),
          };
        }
        return {
          ...base,
          type: "text",
          content: response.data.message || response.title || "No data available.",
        };
      }

      case "list":
        return {
          ...base,
          type: "list",
          content: response.data.message || response.data.items?.join("\n") || "",
          listItems: response.data.items || [],
        };

      case "text":
      default:
        return {
          ...base,
          type: "text",
          content: response.data.message || response.title || "No response.",
        };
    }
  };

  const MAX_QUERY_LENGTH = 2000;

  const processQuery = async (query: string) => {
    if (!currentTenantId) {
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        content: "No tenant selected. Please select an organization first.",
        sender: "system",
        timestamp: formatTime(),
        type: "error",
        errorLevel: "warning",
      };
      setMessages((prev) => [...prev, errorMsg]);
      return;
    }

    if (query.length > MAX_QUERY_LENGTH) {
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        content: `Query too long. Maximum ${MAX_QUERY_LENGTH} characters.`,
        sender: "system",
        timestamp: formatTime(),
        type: "error",
        errorLevel: "warning",
      };
      setMessages((prev) => [...prev, errorMsg]);
      return;
    }

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      content: query,
      sender: "user",
      timestamp: formatTime(),
      type: "text",
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsTyping(true);

    try {
      const result = await dataQueryChat({
        tenant_id: currentTenantId,
        query,
        conversation_history: buildConversationHistory(),
      });

      setIsTyping(false);

      if (result.success && result.data?.response) {
        setIsOffline(false);
        const responseMessage = mapResponseToMessage(result.data.response);
        setMessages((prev) => [...prev, responseMessage]);
      } else {
        // Parse error details from backend
        const details = typeof result.details === 'object' ? result.details : {};
        const retryable = details.retryable !== false;
        const errorCode = details.errorCode as string | undefined;

        // Only show offline for actual server/connectivity issues
        const isServerIssue = ['server_error', 'timeout', 'connection_error'].includes(errorCode || '');
        setIsOffline(isServerIssue);

        const errorMsg: ChatMessage = {
          id: crypto.randomUUID(),
          content: result.error || "Failed to process your query. Please try again.",
          sender: "system",
          timestamp: formatTime(),
          type: "error",
          errorLevel: "error",
          retryQuery: retryable ? query : undefined,
          retryable,
        };

        // Flag the user message if content policy violation (exclude from future AI context)
        if (errorCode === 'content_policy_violation') {
          setMessages((prev) => [
            ...prev.map((m) =>
              m.id === userMessage.id ? { ...m, flagged: true } : m
            ),
            errorMsg,
          ]);
        } else {
          setMessages((prev) => [...prev, errorMsg]);
        }
      }
    } catch {
      setIsTyping(false);
      setIsOffline(true);
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        content: "Service is currently unavailable. Please check your connection and try again.",
        sender: "system",
        timestamp: formatTime(),
        type: "error",
        errorLevel: "error",
        retryQuery: query,
        retryable: true,
      };
      setMessages((prev) => [...prev, errorMsg]);
    }
  };

  const handleRetry = (query: string) => {
    processQuery(query);
  };

  const handleSend = () => {
    if (!input.trim() || isTyping) return;
    const query = input.trim();
    setInput("");
    processQuery(query);
  };

  const handlePromptClick = (prompt: string) => {
    if (isTyping) return;
    processQuery(prompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div
      className={`fixed z-[100] flex flex-col rounded-[20px] bg-white shadow-xl transition-all duration-300 ease-out dark:bg-navy-800
        bottom-[100px] right-[35px] h-[700px] w-[590px]
        md-max:bottom-0 md-max:left-0 md-max:right-0 md-max:top-0 md-max:h-full md-max:w-full md-max:rounded-none
        ${
          isOpen
            ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-4 scale-95 opacity-0"
        }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-navy-700">
        <div>
          <h2 className="text-lg font-bold text-navy-700 dark:text-white">
            AI Data Query
          </h2>
          <div className="flex items-center gap-2">
            <div
              className={`h-2 w-2 rounded-full ${
                isOffline ? "bg-red-500" : "bg-green-500"
              }`}
            />
            <span className="text-xs text-gray-400">
              {isOffline ? "Offline" : "Active"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onClose}
            aria-label="Minimize chat panel"
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-navy-700"
          >
            <MdRemove className="h-5 w-5 text-gray-500 dark:text-white" />
          </button>
        </div>
      </div>

      {/* Messages or Empty State */}
      <div className="flex-1 overflow-hidden px-4 pt-3" aria-live="polite">
        {isEmpty && !isTyping ? (
          <EmptyState onPromptClick={handlePromptClick} />
        ) : (
          <Scrollbars
            ref={scrollRef}
            renderTrackVertical={renderTrackMessages}
            renderThumbVertical={renderThumbMessages}
            renderView={renderViewMessages}
          >
            <div className="pb-2">
              {messages.map((msg, index) => {
                const currentDate = new Date(msg.timestamp).toLocaleDateString("en-US");
                const prevDate = index > 0
                  ? new Date(messages[index - 1].timestamp).toLocaleDateString("en-US")
                  : null;
                const showSeparator = currentDate !== prevDate;

                return (
                  <React.Fragment key={msg.id}>
                    {showSeparator && <DateSeparator label={getDateLabel(msg.timestamp)} />}
                    <QueryMessage message={msg} onRetry={handleRetry} />
                  </React.Fragment>
                );
              })}
              {isTyping && <TypingIndicator />}
            </div>
          </Scrollbars>
        )}
      </div>

      {/* Suggested prompts (shown after messages exist) */}
      {!isEmpty && !isTyping && (
        <div className="flex gap-1.5 overflow-x-auto border-t border-gray-100 px-4 py-2 dark:border-navy-700">
          {suggestedPrompts.map((prompt) => (
            <button
              key={prompt}
              onClick={() => handlePromptClick(prompt)}
              className="shrink-0 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-500 transition hover:border-brand-500 hover:text-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400 dark:hover:border-brand-400 dark:hover:text-brand-400"
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-gray-200 px-4 py-3 dark:border-navy-700">
        <div className="flex items-center gap-2">
          <div className="flex h-[44px] w-full items-center rounded-full bg-lightPrimary dark:bg-navy-700">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Write your message..."
              aria-label="Data query message input"
              className="h-full w-full rounded-full bg-lightPrimary px-4 text-sm text-navy-700 outline-none dark:bg-navy-700 dark:text-white dark:placeholder:text-gray-400"
              type="text"
              disabled={isTyping}
            />
          </div>
          <button
            onClick={handleSend}
            disabled={isTyping}
            aria-label="Send message"
            className="flex h-[44px] w-[44px] min-w-[44px] items-center justify-center rounded-full bg-brand-500 text-white transition duration-200 hover:cursor-pointer hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-brand-400 dark:hover:bg-brand-300"
          >
            <IoPaperPlane />
          </button>
        </div>
      </div>
    </div>
  );
}

export default DataQueryPanel;
